import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import clsx from 'clsx';
import styles from './AddEmergencyAgSnFormForPlan.module.css';
import { Modal } from '@material-ui/core';
import { reasons, agencies } from '../../staticData';
import { API } from '../../apis/api';
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  AGGREGATES,
} from '../../constants';
import {
  isPositiveInteger,
  isQuantityValid,
} from '../../utils/utils';
import './AddEmergencyAgSnFormForPlan.module.css';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import {
  CustomDatePicker,
  CustomSelect,
  ValidatingTextField,
} from '../FormComponents';
import { useErrorHandler } from 'react-error-boundary';
import { buildErrorMessage } from '../../apis/calls';

const AddEmergencyAsnForm = ({
  handleClose,
  currentDate,
  open,
  plant,
  plantName,
  line,
  lineName,
  plantOptions,
  aggregate,
  loadAggregatePlanForAMonth,
  lastEmergencyAgSnDate
}) => {
  const {
    showError,
    showPopup
  } = usePopupManager();

  const handleError = useErrorHandler();

  const formContainerRef = useRef(null);
  const [additionalQuantity, setAdditionalQuantity] = useState('');
  const [planDate, setPlanDate] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const [reason, setReason] = useState('');
  const [destination, setDestination] = useState('');
  const [agency, setAgency] = useState('');
  const [comment, setComment] = useState('');
  const [colour, setColour] = useState('');
  const [highlightMandatoryFields, setHighlightMandatoryFields] = useState(
    false,
  );
  const [colours, setColours] = useState({ loading: false, data: [] });
  const formatOptionsForSelect = (...options) =>
    options.map(option => ({ value: option, label: option }));

  const reasonOptions = formatOptionsForSelect(...reasons);
  const agencyOptions = formatOptionsForSelect(...agencies);
  const handleSelectionCurry = setState => valueObject => setState(valueObject);

  const loadData = async (setState, url, params) => {
    try {
      const response = await API.get(url, {
        params: params,
      });
      setState({ loading: false, data: response.data });
    } catch (error) {
      handleError(error);
    }
  };

  const loadColours = async () => {
    return loadData(setColours, API_RESOURCE_URLS.COLOURS);
  };

  const resetState = async () => {
    setAdditionalQuantity(null);
    setPlanDate(null);
    setReason(null);
    setAgency(null);
    setComment(null);
    setColour(null);
    setHighlightMandatoryFields(null);
    setPartNumber(null);
    setDestination(null);
  };
  useEffect(() => {
    if (open) {
      loadColours();
    } else {
      resetState();
    }
  }, [open, currentDate, plant, line]);

  const getColourOptions = () => {
    return colours.data.map(({ colourCode, colourDescription }) => ({
      value: `(${colourCode})`,
      label: `${colourDescription} (${colourCode})`,
    }));
  };

  const validateFields = fields => {
    const {
      ...requiredFields
    } = fields;
    const hasMissingRequiredFields = Object.values(requiredFields).some(
      field => !field,
    );
    if (hasMissingRequiredFields) {
      return false;
    }
    const { additionalQuantity } = fields;
    if (!isPositiveInteger(additionalQuantity)) return false;
    if (!isQuantityValid(additionalQuantity)) return false;
    return true;
  };

  const checkForMandatoryFielsForEmegrencyAgSn = () => {
    const areFieldsValid = validateFields({
      reason,
      agency,
      additionalQuantity,
      planDate,
      partNumber
    });
    if (!areFieldsValid) {
      setHighlightMandatoryFields(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    const payload = {
      plant,
      line,
      partNumber: partNumber,
      destination: destination.value,
      plannedDate: planDate,
      quantity: Number(additionalQuantity),
      colourCode: colour?.value ? colour.value.replace(/[\(\)']/g, '') : null,
      reason: reason.value,
      agency: agency.value,
      comment,
    }
    try {
      await API.patch(
        API_RESOURCE_URLS.addEmergencyAgsn(
          plant,
          line,
        ),
        payload,
      );
      handleClose();
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: DISPLAY_MESSAGES.EMERGENCY_AGSN_PLAN_SUCCESS,
      });
      loadAggregatePlanForAMonth();
    } catch (error) {
      const errorMessage = JSON.parse(error.request.responseText).errorMessage || DISPLAY_MESSAGES.AGSN_ADD_FAILURE
      showError({
        contextText: errorMessage,
        info: '',
        error: buildErrorMessage(error),
      });
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} disableBackdropClick>
        <div className={styles.addAsn} ref={formContainerRef}>
          <div className={styles.addAsnTitle}>
            <span>Add Emergency AgSn</span>
            <div className={styles.plantInfo}>
              Line <strong>{lineName}</strong>
            </div>
            <div className={styles.plantInfo}>
              Plant <strong>{plantName}</strong>
            </div>
          </div>
          <div className={styles.frm}>
            <div className={styles.formSec}>
              <p className={styles.note}>
                Note: These Emergency AGSN(s) will be added at the end of the Lineup for a
                selected Plan date.
                <span className={styles.optionalFields}>
                  ** Optional Fields
                </span>
              </p>

              <span className={styles.rowTitleTop}>Basic Info</span>
              <div className={styles.leftPart}>
                <div className={styles.formRow}>
                  <label className={styles.label}>Part Number</label>
                  <input
                    name="partNumber"
                    type="text"
                    data-testid="part-number"
                    className={styles.partNumber}
                    value={partNumber}
                    onChange={e => setPartNumber(e.target.value)}
                  />
                </div>

                <div className={styles.dateField}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    Plan date
                  </label>
                  <CustomDatePicker
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    autoOk
                    inputVariant="outlined"
                    value={planDate}
                    onChange={date => {
                      setPlanDate(date.format(DATE_FORMAT.ISO));
                    }}
                    inputProps={{
                      className: clsx(styles.select, styles.dateInput),
                    }}
                    emptyLabel="dd/mm/yyyy"
                    minDate={
                      process.env.NODE_ENV === 'test'
                        ? moment(1)
                        : currentDate || moment()
                    }
                    maxDate={
                      process.env.NODE_ENV === 'test'
                        ? moment('3020', 'YYYY')
                        : lastEmergencyAgSnDate || moment()
                    }
                    format="DD/MM/yyyy"
                    data-testid="date-picker-div"
                  />
                </div>
                <div className={styles.col8}>
                  <div className={styles.formRow}>
                    <label className={styles.label}>Destination</label>
                    <CustomSelect
                      name="destination"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={plantOptions}
                      className={clsx(styles.select, styles.sel1)}
                      value={destination}
                      onChange={handleSelectionCurry(setDestination)}
                    />
                  </div>
                </div>
              </div>
              <div className={clsx(styles.formRow, styles.leftPart)}>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    Quantity
                  </label>
                  <ValidatingTextField
                    isMandatory
                    validationFn={isPositiveInteger}
                    markIfEmpty={highlightMandatoryFields}
                    isQuantityValid={isQuantityValid(additionalQuantity)}
                    validationHelperText={
                      DISPLAY_MESSAGES.POSITIVE_NUMBER_VALIDATION_WARNING
                    }
                    quantityValidationText={DISPLAY_MESSAGES.INVALID_INPUT}
                    variant="outlined"
                    value={additionalQuantity}
                    onChange={e => setAdditionalQuantity(e.target.value)}
                    placeholder="(Enter value)"
                    className={styles.textField}
                    inputProps={{
                      className: styles.textInput,
                      'data-testid': 'additional-quantity-input',
                    }}
                    FormHelperTextProps={{
                      className: styles.helperText,
                    }}
                  />
                </div>
                {[AGGREGATES.CAB, AGGREGATES.BIW, AGGREGATES.PAINT].includes(aggregate) && (
                  <div className={styles.col5}>

                    <label className={styles.label}>
                      Colour Code
                    </label>
                    <CustomSelect
                      name="colour"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={getColourOptions()}
                      className={clsx(styles.select, styles.sel1)}
                      value={colour}
                      onChange={handleSelectionCurry(setColour)}
                    />
                  </div>
                )}
              </div>
              <div className={clsx(styles.formSec, styles.vcDetail)}>
                <span className={styles.rowTitle}>Revision Details </span>
                <div className={styles.col6}>
                  <div className={styles.formRow}>
                    <label className={styles.label}>Reason</label>
                    <CustomSelect
                      name="reason"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={reasonOptions}
                      className={clsx(styles.select, styles.sel1)}
                      value={reason}
                      onChange={handleSelectionCurry(setReason)}
                    />
                  </div>
                  <div className={styles.formRow}>
                    <label className={styles.label}>Agency</label>
                    <CustomSelect
                      name="agency"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={agencyOptions}
                      className={clsx(styles.select, styles.sel1)}
                      value={agency}
                      onChange={handleSelectionCurry(setAgency)}
                    />
                  </div>
                </div>
                <div className={styles.col7}>
                  <div className={styles.formRow}>
                    <label className={clsx(styles.label, styles.commentsLabel)}>
                      Comments<br></br> (Optional)
                    </label>
                    <textarea
                      className={styles.comments}
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.buttons}>
              <button onClick={handleClose} className={styles.cancel}>
                Cancel
              </button>
              <button
                className={styles.submit}
                onClick={async e => {
                  if (e.detail > 1) {
                    console.log('A double-click has been registered');
                    return;
                  }
                  const checkAgSn = checkForMandatoryFielsForEmegrencyAgSn();
                  if (checkAgSn) {
                    handleSubmit();
                  }
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

AddEmergencyAsnForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  currentDate: PropTypes.objectOf(moment),
  updatePlanRows: PropTypes.func.isRequired,
  skipToLastPage: PropTypes.func.isRequired,
  plant: PropTypes.string.isRequired,
  plantName: PropTypes.string.isRequired,
  line: PropTypes.string.isRequired,
  lineName: PropTypes.string.isRequired,
  loadAggregatePlanForAMonth: PropTypes.func,
  lastEmergencyAgSnDate: PropTypes.objectOf(moment),
};

export default AddEmergencyAsnForm;
